import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRightIcon } from 'lucide-react';
import { Container, Section, SectionHeader, Kicker, Card, Reveal, PullQuote } from '../components/ui/Primitives';
import { Button } from '../components/ui/Button';
import { SERVICE_LINES } from '../data/services';
export function ServicesOverview() {
  return <>
      <section className="surface-dark" data-id="element-1576" data-mp="cvvebahpih9m">
        <Container className="py-16 sm:py-24" data-id="element-1577" data-mp="gvvtixcmqwhq">
          <Kicker data-id="element-1578" data-mp="gv482fcqyf56">Services</Kicker>
          <h1 className="mt-4 max-w-3xl font-display text-[34px] leading-tight text-white sm:text-[46px]" data-id="element-1579" data-mp="lu6zuy3puov7">
            Three lines, one method, in the order the work usually needs
          </h1>
          <p className="mt-6 max-w-2xl text-[17px] text-white/72" data-id="element-1580" data-mp="vsmv9d9s0kai">
            Intelligence establishes where the value is. Growth builds the route to it. AI makes the whole thing
            repeatable without adding headcount. You can start anywhere. Most clients start with Intelligence.
          </p>
        </Container>
      </section>

      <Section data-id="element-1581" data-mp="b0k5hg6yrx5g">
        <div className="grid gap-6 lg:grid-cols-3" data-id="element-1582" data-mp="b1d6s1n5lug7">
          {SERVICE_LINES.map((line, index) => <Reveal key={line.id} delay={index * 0.06} data-id="element-1583" data-mp="67qzyq3o8ksb" data-mpi={index}>
              <Card className="flex h-full flex-col" data-id="element-1584" data-mp="197d8nh974yw">
                <span className="font-display text-[13px] text-gold" data-id="element-1585" data-mp="17wc6vf70bbh">Step 0{index + 1}</span>
                <h2 className="mt-3 font-display text-[26px] leading-tight text-ink" data-id="element-1586" data-mp="3se74a22861qo">{line.name}</h2>
                <p className="mt-3 text-[15px] text-body" data-id="element-1587" data-mp="twjzsxnezcs7">{line.promise}</p>
                <p className="mt-5 text-[11px] font-semibold uppercase tracking-[0.18em] text-muted" data-id="element-1588" data-mp="p0uu0ckswzng">Who it is for</p>
                <p className="mt-2 text-[14px] text-body" data-id="element-1589" data-mp="jyni1mzz1jl4">{line.who}</p>
                <p className="mt-5 text-[11px] font-semibold uppercase tracking-[0.18em] text-muted" data-id="element-1590" data-mp="fy3i6bo0k1va">The outcome</p>
                <p className="mt-2 text-[14px] text-body" data-id="element-1591" data-mp="34s8y8cndy6wv">{line.outcome}</p>
                <Link to={`/services/${line.id}`} className="mt-auto inline-flex items-center gap-2 pt-7 text-[15px] font-medium text-royal underline-offset-4 hover:underline" data-id="element-1592" data-mp="30o18qd93smxl">
                  Explore this line
                  <ArrowRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-1593" data-mp="2s8c1c7pv09ic" />
                </Link>
              </Card>
            </Reveal>)}
        </div>
      </Section>

      <Section tone="champagne" aria-labelledby="cavalla-gov" data-id="element-1594" data-mp="2x8zqsoyfwiag">
        <div className="grid gap-10 lg:grid-cols-[1.1fr_0.9fr] lg:items-center" data-id="element-1595" data-mp="2jf04rpshzv42">
          <div data-id="element-1596" data-mp="2o7jshfkwg6ml">
            <Kicker data-id="element-1597" data-mp="2o79xteowtbak">Our platform for government</Kicker>
            <h2 id="cavalla-gov" className="mt-4 font-display text-[28px] leading-tight text-ink sm:text-[36px]" data-id="element-1598" data-mp="29hudohjvw43u">
              Cavalla Gov
            </h2>
            <p className="mt-5 max-w-xl text-[17px] text-body" data-id="element-1599" data-mp="2egylp4v2nvsl">
              A platform in its own right, distinct from the three advisory lines but drawing on all of them. It
              gives a ministry of finance one live picture of how the national development plan is being
              delivered, and where development partner funding lands against it.
            </p>
          </div>
          <div className="lg:justify-self-end" data-id="element-1600" data-mp="1gwp023pfwy5a">
            <Button to="/cavalla-gov" size="lg" shape="pill" data-id="element-1601" data-mp="1uun6mmxirqa8">
              See the platform
            </Button>
          </div>
        </div>
      </Section>

      <Section tone="mist" data-id="element-1602" data-mp="1puhdt3fijxqk">
        <div className="grid gap-12 lg:grid-cols-2" data-id="element-1603" data-mp="24t30uyruffny">
          <SectionHeader kicker="How they connect" title="The path to depth" lead="Each line stands alone. Together they compound, because the evidence gathered in one becomes the input to the next." data-id="element-1604" data-mp="24ri6b95j3wq3" />
          <div className="space-y-6" data-id="element-1605" data-mp="29k1jw647uruw">
            <PullQuote data-id="element-1606" data-mp="29l2vqct23ido">
              A market study that never becomes a plan is an expensive opinion. A plan without evidence is a
              guess with a deadline.
            </PullQuote>
            <p className="text-[15px] text-body" data-id="element-1607" data-mp="2iliwheif0beu">
              We built the three lines so a client can begin with the question they actually have, rather than the
              engagement a firm wants to sell. If you know your market and need demand, start with Growth. If you
              are being pressed on AI and cannot tell hype from opportunity, start with AI.
            </p>
            <Button to="/augmented-intelligence" variant="secondary" data-id="element-1608" data-mp="3ge8u8ypzlw4i">
              Read our method
            </Button>
          </div>
        </div>
      </Section>

      <section className="surface-dark" data-id="element-1609" data-mp="2npbmvqnbdjqj">
        <Container className="py-16" data-id="element-1610" data-mp="2nqcz3yg2gc9u">
          <div className="flex flex-col items-start justify-between gap-8 lg:flex-row lg:items-center" data-id="element-1611" data-mp="2nreay54wp2sm">
            <h2 className="max-w-xl font-display text-[28px] leading-tight text-white" data-id="element-1612" data-mp="2sqhefl58n0yh">
              Not certain which line fits your question
            </h2>
            <div className="flex flex-col gap-3 sm:flex-row" data-id="element-1613" data-mp="7xdzk6jcfi1i">
              <Button to="/contact" size="lg" data-id="element-1614" data-mp="cx0f4mmn9adh">Book a discovery call</Button>
              <Button to="/assessment" variant="reversed" size="lg" data-id="element-1615" data-mp="cz3cjrq4m5s8">Take the assessment</Button>
            </div>
          </div>
        </Container>
      </section>
    </>;
}